// The ledger entries of a migration, as pure line lists (ADR-0048; spec/06 §2.1, §2.3).
//
// A carried-over balance is a real liability from the moment it is imported: an unclaimed ₹450 is still owed. So the
// import posts it, against an organisation-level **opening balance** account — the value the café brought from its
// previous system — and never against a tender, a drawer or a sales account: the old system took the money, so no
// cash moves today, nothing is a sale, and the day's takings are untouched.
//
//   opening_balance_import    INR  opening_balance[org] +C · carried_over_cash[ip] −C   (or carried_over_cash_tax_paid)
//                             SEC  time_issued[org] +S · carried_over_time[ip] −S
//   opening_balance_reversal  the exact negation of the import, for an unclaimed record undone
//   carried_over_claim        INR  carried_over_cash[ip] +C · wallet_cash[player] −C      (tax-paid to tax-paid)
//                             SEC  carried_over_time[ip] +S · pass_time[holding] −S        (the claim, a follow-up)
//
// Every list balances per unit (invariant 1, `assertBalancedPerUnit`); the server maps each abstract account to its
// `LedgerAccountRef`. Bonus money is never here: it is dropped (owner's decision 4).
import { DomainError } from "../errors.js";
function assertCarriedOver(amounts) {
    for (const [what, value] of [
        ['cashMinor', amounts.cashMinor],
        ['timeSeconds', amounts.timeSeconds],
    ]) {
        if (!Number.isSafeInteger(value))
            throw new DomainError('not-an-integer', `${what} must be a whole number, got ${value}.`, { what, value });
        if (value < 0)
            throw new DomainError('out-of-range', `${what} cannot be negative: a debt is listed, never carried over.`, { what, value });
    }
}
/** The import entry's lines for one record. Empty when it carries nothing (a record with no balance posts no entry). */
export function openingBalanceImportLines(amounts) {
    assertCarriedOver(amounts);
    const lines = [];
    if (amounts.cashMinor > 0) {
        const cash = BigInt(amounts.cashMinor);
        lines.push({ account: 'opening_balance', unit: 'INR', amount: cash });
        lines.push({ account: amounts.cashTaxPaid ? 'carried_over_cash_tax_paid' : 'carried_over_cash', unit: 'INR', amount: -cash });
    }
    if (amounts.timeSeconds > 0) {
        const seconds = BigInt(amounts.timeSeconds);
        lines.push({ account: 'time_issued', unit: 'SEC', amount: seconds });
        lines.push({ account: 'carried_over_time', unit: 'SEC', amount: -seconds });
    }
    return lines;
}
/** The undo of an import: every line negated, in the same order (spec/06 §2.2: corrections are reversing entries). */
export function reversalLines(lines) {
    return lines.map((line) => ({ ...line, amount: -line.amount }));
}
/**
 * The claim entry's lines (built by the claiming feature): what is still held for the record moves to the player's
 * wallet — tax-paid money to the tax-paid balance, so it is never taxed twice — and the time to a new pass holding.
 * `held` is what the record's accounts hold at the moment of the claim (natural, ≥ 0).
 */
export function carriedOverClaimLines(held) {
    const lines = [];
    for (const [what, value] of Object.entries(held)) {
        if (!Number.isSafeInteger(value) || value < 0)
            throw new DomainError('out-of-range', `${what} must be a whole number ≥ 0, got ${value}.`, { what, value });
    }
    if (held.cashMinor > 0) {
        lines.push({ account: 'carried_over_cash', unit: 'INR', amount: BigInt(held.cashMinor) });
        lines.push({ account: 'wallet_cash', unit: 'INR', amount: -BigInt(held.cashMinor) });
    }
    if (held.cashTaxPaidMinor > 0) {
        lines.push({ account: 'carried_over_cash_tax_paid', unit: 'INR', amount: BigInt(held.cashTaxPaidMinor) });
        lines.push({ account: 'wallet_cash_tax_paid', unit: 'INR', amount: -BigInt(held.cashTaxPaidMinor) });
    }
    if (held.timeSeconds > 0) {
        lines.push({ account: 'carried_over_time', unit: 'SEC', amount: BigInt(held.timeSeconds) });
        lines.push({ account: 'pass_time', unit: 'SEC', amount: -BigInt(held.timeSeconds) });
    }
    return lines;
}
/** The day a carried-over time pass lapses: `validityDays` after the switch-over day (owner's decision 3: 180). */
export function carriedOverTimeValidUntil(switchDay, validityDays) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(switchDay);
    if (match === null)
        throw new DomainError('invalid-moment', `"${switchDay}" is not a calendar date (YYYY-MM-DD).`, { switchDay });
    if (!Number.isSafeInteger(validityDays) || validityDays < 1)
        throw new DomainError('out-of-range', `validityDays must be ≥ 1, got ${validityDays}.`, { validityDays });
    const instant = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) + validityDays * 86_400_000;
    return new Date(instant).toISOString().slice(0, 10);
}
/**
 * Whether a move asks "did the old system charge GST at top-up?" (ADR-0048 §10): only of a venue registered for GST on
 * the switch-over day. A venue that is not registered sells with plain bills and charges no GST, so the question means
 * nothing there and every balance moves as ordinary money.
 */
export function migrationAsksGstAtTopUp(taxMode) {
    return taxMode !== 'unregistered';
}
/**
 * Whether a move's money is carried over as tax-paid: only when the venue is registered on the switch-over day and HQ
 * answered yes. A yes for a venue that is not registered is refused (`out-of-range`): the server never stores one.
 */
export function carriedOverAsTaxPaid(taxMode, gstPaidAtTopUp) {
    if (!migrationAsksGstAtTopUp(taxMode)) {
        if (gstPaidAtTopUp === true) {
            throw new DomainError('out-of-range', 'A venue that is not registered for GST cannot carry money over as GST paid.', { taxMode });
        }
        return false;
    }
    return gstPaidAtTopUp === true;
}
//# sourceMappingURL=ledger.js.map